import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Input } from 'antd';
import { SearchOutlined, DownOutlined } from '@ant-design/icons';
import { useGetLeadGroupsQuery } from '../../services/leadGroups';
import './styles.scss';

interface GroupVal { id: string; name: string; }

interface Props {
  value:    GroupVal | null;
  onChange: (g: GroupVal | null) => void;
  onOpenChange?: (open: boolean) => void;
}

const ColumnGroupSelect = ({ value, onChange, onOpenChange }: Props) => {
  const [open, setOpen] = useState(false);

  useEffect(() => { onOpenChange?.(open); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const [search, setSearch] = useState('');
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const { data: resp, isFetching } = useGetLeadGroupsQuery(undefined, { skip: !open });
  const groups = ((resp?.data ?? resp ?? []) as any[])
    .map((g, i) => ({ id: g._id ?? g.id ?? `g-${i}`, name: g.name ?? '—', color: g.colour ?? g.color ?? '#64748b' }))
    .filter(g => g.name.toLowerCase().includes(search.toLowerCase()));

  // Position the dropdown under the trigger (fixed, so table scroll can't clip it).
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const place = () => {
      const r = triggerRef.current!.getBoundingClientRect();
      const width = 300;
      const left = Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8));
      setCoords({ top: r.bottom + 6, left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [open]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const choose = (g: GroupVal | null) => { onChange(g); setOpen(false); };

  return (
    <>
      <span
        ref={triggerRef}
        className="cgs-trigger"
        onClick={() => setOpen(o => !o)}
      >
        {value?.name || 'SELECT GROUP'} <DownOutlined />
      </span>

      {open && createPortal(
        <div ref={popRef} className="cgs-pop" style={{ top: coords.top, left: coords.left }}>
          <div className="cgs-search">
            <Input
              placeholder="Search groups"
              prefix={<SearchOutlined style={{ color: 'var(--muted)' }} />}
              value={search}
              onChange={e => setSearch(e.target.value)}
              allowClear
            />
          </div>
          <div className="cgs-list">
            <div className={`cgs-item ${!value ? 'active' : ''}`} onClick={() => choose(null)}>
              <span className="cgs-color" style={{ background: 'var(--muted)' }} />
              <span className="cgs-name">All Groups</span>
            </div>
            {isFetching && <div className="cgs-state">Loading…</div>}
            {!isFetching && groups.map(g => (
              <div
                key={g.id}
                className={`cgs-item ${value?.id === g.id ? 'active' : ''}`}
                onClick={() => choose({ id: g.id, name: g.name })}
              >
                <span className="cgs-color" style={{ background: g.color }} />
                <span className="cgs-name">{g.name}</span>
              </div>
            ))}
            {!isFetching && groups.length === 0 && <div className="cgs-state">No groups found</div>}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default ColumnGroupSelect;
